import type {
  propsApiContract,
  propsApiContractCard,
  propsApiContractIndexer,
  propsApiContractModality,
  propsApiContractStatus,
  propsApiExtractionDraft,
  propsApiInstallment,
  propsApiInstallmentStatus,
} from '~/services/http/interfaces';

import type {
  Contract,
  ContractDraft,
  ContractInstallment,
  ContractStatus,
  InstallmentStatus,
  propsContractFrequency,
  propsContractIndexer,
  propsContractModality,
} from './interfaces';

const MODALITIES: readonly propsContractModality[] = [
  'custeio',
  'investimento',
  'capital_giro',
  'cprf',
  'comercializacao',
  'outros',
];

const modalityToApp = (m: propsApiContractModality): propsContractModality =>
  MODALITIES.includes(m as propsContractModality)
    ? (m as propsContractModality)
    : 'outros';

const modalityToApi = (m: propsContractModality): propsApiContractModality =>
  MODALITIES.includes(m) ? (m as propsApiContractModality) : 'outros';

const indexerToApp: Record<propsApiContractIndexer, propsContractIndexer> = {
  pre_fixed: 'pre-fixed',
  cdi: 'cdi',
  ipca: 'ipca',
  selic: 'selic',
};

const indexerToApi: Record<propsContractIndexer, propsApiContractIndexer> = {
  'pre-fixed': 'pre_fixed',
  cdi: 'cdi',
  ipca: 'ipca',
  selic: 'selic',
};

const installmentStatusToApp = (s: propsApiInstallmentStatus): InstallmentStatus => {
  if (s === 'cancelled') return 'future';
  return s;
};

const contractStatusToApp = (s: propsApiContractStatus): ContractStatus => {
  if (s === 'cancelled') return 'completed';
  return s;
};

export function mapInstallment(
  api: propsApiInstallment,
  contractId: string,
): ContractInstallment {
  return {
    id: api.id,
    contractId,
    index: api.index,
    dueDate: api.dueDate,
    amount: api.amountCents,
    principal: api.principalCents ?? undefined,
    interest: api.interestCents ?? undefined,
    status: installmentStatusToApp(api.status),
    paidAt: api.paidAt ?? undefined,
    paymentId: api.paymentId ?? undefined,
    paidAmount: api.paidAmountCents ?? undefined,
    description: api.description ?? undefined,
  };
}

export function mapContractCard(card: propsApiContractCard): Contract {
  const nowIso = new Date().toISOString();
  return {
    id: card.id,
    institution: {
      id: card.institution.id,
      name: card.institution.name,
      shortCode: card.institution.shortCode,
    },
    modality: modalityToApp(card.modality),
    amortizationType: card.amortizationType,
    contractNumber: card.contractNumber,
    amountFinanced: card.amountFinancedCents,
    amountTotal: card.amountTotalCents,
    amountPaid: card.amountPaidCents,
    amountRemaining: card.amountRemainingCents,
    interestRate: 0,
    indexer: 'pre-fixed',
    frequency: 'monthly',
    installmentsTotal: card.installmentsTotal,
    installmentsPaid: card.installmentsPaid,
    startDate: nowIso,
    endDate: nowIso,
    cropDescription: card.cropDescription ?? undefined,
    riskLevel: card.riskLevel,
    status: contractStatusToApp(card.status),
    createdAt: nowIso,
    updatedAt: nowIso,
  };
}

export function mapContract(api: propsApiContract): Contract {
  return {
    id: api.id,
    institution: {
      id: api.institution.id,
      name: api.institution.name,
      shortCode: api.institution.shortCode,
    },
    modality: modalityToApp(api.modality),
    amortizationType: api.amortizationType,
    cedulaType: api.cedulaType ?? undefined,
    contractNumber: api.contractNumber,
    amountFinanced: api.amountFinancedCents,
    amountTotal: api.amountTotalCents,
    amountPaid: api.amountPaidCents,
    amountRemaining: api.amountRemainingCents,
    interestRate: api.interestRate,
    indexer: indexerToApp[api.indexer],
    rateResetMonths: api.rateResetMonths ?? undefined,
    lastRepricedAt: api.lastRepricedAt ?? undefined,
    frequency: api.frequency as propsContractFrequency,
    installmentsTotal: api.installmentsTotal,
    installmentsPaid: api.installmentsPaid,
    startDate: api.startDate,
    endDate: api.endDate,
    guarantee: api.guarantee ?? undefined,
    cropDescription: api.cropDescription ?? undefined,
    riskLevel: api.riskLevel,
    status: contractStatusToApp(api.status),
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
}

export function mapExtractionDraftToApp(
  e: propsApiExtractionDraft,
  fallbackName?: string,
): ContractDraft {
  const institutionName =
    e.institutionMatch?.name ?? e.institutionHint ?? fallbackName;
  return {
    institution: institutionName
      ? {
          id: e.institutionMatch?.id,
          name: institutionName,
          shortCode: e.institutionMatch?.shortCode,
        }
      : undefined,
    modality: e.modality ? modalityToApp(e.modality) : undefined,
    amortizationType: e.amortizationType ?? undefined,
    cedulaType: e.cedulaType ?? undefined,
    contractNumber: e.contractNumber ?? undefined,
    amountFinanced: e.amountFinancedCents ?? undefined,
    interestRate: e.interestRate ?? undefined,
    indexer: e.indexer ? indexerToApp[e.indexer] : undefined,
    frequency: e.frequency as propsContractFrequency | undefined,
    installmentsTotal: e.installmentsTotal ?? undefined,
    startDate: e.startDate ?? undefined,
    endDate: e.endDate ?? undefined,
    guarantee: e.guarantee ?? undefined,
    cropDescription: e.cropDescription ?? undefined,
    installmentSchedule:
      e.installmentSchedule?.map((s) => ({
        dueDate: s.dueDate,
        amountCents: s.amountCents,
        paid: s.paid,
        paidDate: s.paidDate ?? undefined,
      })) ?? undefined,
  };
}

export { modalityToApi, indexerToApi };
